import React, { useState, useEffect } from 'react';

interface AvatarProps {
  expanded: boolean;
  toggleExpand: () => void;
  lastMessage?: string;
  isTyping: boolean;
  selectedAvatar: number;
}

const idleMessages = [
  "Want to tell me about your day? Remember I'm always here",
  "It's important to rest your eyes after long period on the computer",
  "Some stretches after sitting for long period will help freshen up your thinking!"
];

const Avatar: React.FC<AvatarProps> = ({ expanded, toggleExpand, lastMessage, isTyping, selectedAvatar }) => {
  const [animation, setAnimation] = useState('pulse');
  const [showBubble, setShowBubble] = useState(false);
  const [currentIdleMessage, setCurrentIdleMessage] = useState(0);
  
  // Handle idle messages rotation
  useEffect(() => {
    if (!expanded && !isTyping && !lastMessage) {
      const showMessageInterval = setInterval(() => {
        setShowBubble(true);
        setCurrentIdleMessage((prev) => (prev + 1) % idleMessages.length);
        
        // Hide the message after 5 seconds
        const hideTimer = setTimeout(() => {
          setShowBubble(false);
        }, 5000);
        
        return () => clearTimeout(hideTimer);
      }, 15000); // Show new message every 15 seconds
      
      return () => clearInterval(showMessageInterval);
    }
  }, [expanded, isTyping, lastMessage]);

  // Handle chat messages and typing states
  useEffect(() => {
    if (isTyping) {
      setAnimation('typing');
      setShowBubble(true);
    } else if (lastMessage && !expanded) {
      setAnimation('bounce');
      setShowBubble(true);
      const timer = setTimeout(() => {
        setAnimation('pulse');
        setShowBubble(false);
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setAnimation('pulse');
    }
  }, [lastMessage, isTyping, expanded]);

  return (
    <div className="relative">
      {!expanded && showBubble && (
        <div className="absolute right-full mr-4 top-1/2 -translate-y-1/2">
          <div className="relative bg-white rounded-2xl shadow-lg p-4 w-64">
            {/* Speech bubble pointer */}
            <div className="absolute right-[-8px] top-1/2 -translate-y-1/2 w-4 h-4 bg-white transform rotate-45" />
            
            {/* Content */}
            <div className="relative">
              {isTyping ? (
                <div className="flex space-x-1 justify-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                </div>
              ) : (
                <p className="text-gray-800 text-sm leading-relaxed line-clamp-4">
                  {lastMessage || idleMessages[currentIdleMessage]}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <button
        onClick={toggleExpand}
        className={`
          flex items-center justify-center transition-all duration-300 w-24 h-24
          ${animation === 'pulse' ? 'animate-pulse' : ''}
          ${animation === 'bounce' ? 'animate-bounce' : ''}
          ${animation === 'typing' ? 'animate-ping' : ''}
        `}
      >
        <img 
          src="/images/image___1_-removebg-preview.png"
          alt="AI Avatar" 
          className="w-full h-full object-contain"
        />
      </button>
    </div>
  );
};

export default Avatar;